/**
 * Renders a <nav> with a dynamic list based on conditions.
 * Items causing overflow are moved to a "More" menu.
 *
 * The "More" menu is hidden from screen readers. These users will see a
 * normal <nav> with normal list items.
 *
 * A `ResizeObserver` listens for changes on the <nav> size and recalculates
 * the "More" menu as needed.
 *
 * During the calculation, we use a combination of `hidden` attributes and CSS
 * to hide various elements until the calculation is complete. This helps
 * reduce visual jumping.
 */
import React, { lazy, Suspense, useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useResizeObserver } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { cx } from '@linaria/core'
import { useOverlay, MenuOverlay, ListItem, useMenuOverlay } from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import { useGroupsContext, GroupsNavigation } from '@fs/zion-tree-groups'
import WhatsNew from './WhatsNew'
import NavItem from './NavItem'
import debounce from './debounce'
import useSubnavItems from './useSubNavItems'
import {
  navCss,
  fixedMaxWidthCss,
  listCss,
  listCalculatingCss,
  visibleListItemCss,
  groupsNavListItemCss,
  moreListItemCss,
  visuallyHiddenCss,
  endCss,
  buttonCss,
} from './styles'

// Note: if we were to manage the overlay in this file, the lazy-load of the content of the overlay causes the entire page to re-render for some reason in an actual app. I wasn't able to figure out the reason, but this solution keeps the recents list logic more separate anyway, so I like the solution. If we have a reason we can't do this in the future, this note is to help us remember that it was causing rerenders and how we solved it.
const RecentsListOverlay = lazy(() => import('@fs/zion-tree-recents'))

const pagesWithDisabledNavigation = ['private-people', 'research-tree', 'family', 'following', 'contributions']

export default function SubNav({
  page = 'overview',
  fixedMaxWidth = false,
  showHelpOthers = false,
  readOnlyMode = false,
  className,
  onTreeNavClickOverride,
}) {
  const [t] = useTranslation()

  const recentsOverlay = useOverlay()
  const { canAccessFGT, canAccessCET, groups, currentTreeId, isResearcherGroup, isReadOnlyTree } = useGroupsContext()
  const hasTrees = groups?.some(({ treeIds }) => Boolean(treeIds?.[0]))
  const canAccessGroups = canAccessFGT || canAccessCET
  const showGroupsNavigation = (canAccessGroups && hasTrees) || isReadOnlyTree

  // Nav items are included conditionally
  const items = useSubnavItems({
    recentsOverlay,
    page,
    showHelpOthers,
    canAccessFGT,
    canAccessCET,
    showGroupsNavigation,
    currentTreeId,
    isResearcherGroup,
  })

  // Depending on how much room is available, some nav items will end up under
  // a "More" menu. We need refs to calculate various DOM element dimensions.
  const moreMenuOverlay = useMenuOverlay()
  const nav = useRef()
  const list = useRef()
  const moreListItem = useRef()

  // A count of how many list items will end up in the "More" menu.
  const [moreItemsCount, setMoreItemsCount] = useState(0)
  // When rendering list items, visibly hide any past this index.
  const lastVisibleIndex = items.length - 1 - moreItemsCount

  // These items will render in the "More" menu except the whats new button.
  const moreItems = items.slice(lastVisibleIndex + 1)

  const calculateMoreItems = useCallback(() => {
    if (nav.current && list.current && moreListItem.current) {
      // We need the "More" list item in the layout so we know its width.
      // CSS takes care of visually hiding it.
      moreListItem.current.hidden = false
      // Mark the list as calculating so we affect some other CSS.
      list.current.classList.add(listCalculatingCss)

      // We will read list items starting at the end and working backwards.
      // This will help us short circuit the loop once we find the first item
      // that does not cause overflow.
      let previousItemFit
      // This will hold the new moreListItem value
      let newMoreItemsCount = 0

      // Reverse the list items so we start with the last one.
      const reversedListItemChildren = Array.from(list.current.children).reverse()

      // Ignore the "More" list item
      reversedListItemChildren.shift()

      // Calculating overflow depends on the text direction.
      const dir = window.getComputedStyle(nav.current).direction

      reversedListItemChildren.forEach((listItem, index) => {
        // Ignore the last item and short circuit if the previous item was not
        // overflowing.
        if (previousItemFit || index === reversedListItemChildren.length - 1) return

        // Include the "More" list item width when calculating overflow on all
        // but the last displayed item (i.e., first of this reversed array).
        const includedMoreListItemWidth = index > 0 ? moreListItem.current.offsetWidth : 0

        // Determine if there is overflow.
        if (
          (dir === 'rtl' && listItem.offsetLeft - includedMoreListItemWidth < 0) ||
          (dir === 'ltr' &&
            // The right side of the list item is greater than the available space:
            listItem.offsetLeft + listItem.offsetWidth >
              (listItem.offsetParent?.offsetWidth ?? Infinity) - includedMoreListItemWidth)
        ) {
          newMoreItemsCount++
        } else {
          previousItemFit = true
        }
      })

      // Hide the "More" menu if it will be empty
      if (newMoreItemsCount === 0) moreListItem.current.hidden = true

      // Save the new state.
      setMoreItemsCount(newMoreItemsCount)

      // We are done calculating.
      list.current.classList.remove(listCalculatingCss)
    }
  }, [])

  // Listen for (debounced) changes in nav size
  useResizeObserver(nav, debounce(calculateMoreItems, 150))

  return (
    <nav ref={nav} className={cx(navCss, className)}>
      <ul
        ref={list}
        // Start out with listCalculatingCss so the "More" menu is not
        // visible. `calculateMoreItems` will then remove this class name once
        // it finishes.
        className={cx(listCss, listCalculatingCss, fixedMaxWidth ? fixedMaxWidthCss : undefined)}
      >
        {showGroupsNavigation && (
          <li className={groupsNavListItemCss}>
            <GroupsNavigation
              page={page}
              onListItemClickOverride={onTreeNavClickOverride}
              disableNavigation={pagesWithDisabledNavigation.includes(page)}
            />
          </li>
        )}

        {/* Render all the list items so they are available to the accessibility
        API but visually hide the "More" list items. */}
        {items.map(({ page: itemPage, listItemCss, moreAs, ...item }, index) => (
          <li
            key={itemPage}
            className={cx(visibleListItemCss, listItemCss, index > lastVisibleIndex ? visuallyHiddenCss : undefined)}
          >
            <NavItem {...item} aria-current={itemPage === page ? 'page' : 'false'} />
          </li>
        ))}

        {/* Hide the "More" menu from the accessibility API */}
        <li ref={moreListItem} className={moreListItemCss} aria-hidden="true">
          <MenuOverlay
            menuButton={
              <button type="button" className={buttonCss} onClick={moreMenuOverlay.handleClick}>
                {t('subnav-legacy.more.label')}
                <ArrowCaret direction="down" size="xs" />
              </button>
            }
          >
            {moreItems.map(
              ({
                as: As = ListItem,
                moreAs: MoreAs = As,
                label,
                page: itemPage,
                to,
                linkName,
                'data-testid': dataTestId,
                target,
                external,
                onClick,
              }) => (
                <MoreAs
                  key={itemPage}
                  primaryText={t(label)}
                  to={to}
                  target={target}
                  linkName={linkName}
                  data-testid={dataTestId}
                  selected={itemPage === page}
                  external={external}
                  onClick={onClick}
                />
              )
            )}
          </MenuOverlay>
        </li>

        <li className={endCss}>
          <WhatsNew canAccessFGT={canAccessFGT} canAccessCET={canAccessCET} />
        </li>
      </ul>

      {(recentsOverlay.isOpen || recentsOverlay.transitioning) && (
        <Suspense fallback={null}>
          <RecentsListOverlay overlay={recentsOverlay} readOnlyMode={readOnlyMode} currentTreeId={currentTreeId} />
        </Suspense>
      )}
    </nav>
  )
}

// Stryker disable all
SubNav.propTypes = {
  /** Which page the user is on currently. This will control which tab is highlighted. */
  page: PropTypes.oneOf([
    'overview',
    'pedigree',
    'person',
    'find',
    'following',
    'contributions',
    'private-people',
    'family',
    'research-tree',
  ]).isRequired,

  /** Whether to show the "Help Others" link when possible */
  showHelpOthers: PropTypes.bool,

  /**
   * Whether to apply a 1440px max width and center the navigation.
   * @deprecated Please apply a max-width via CSS if needed. */
  fixedMaxWidth: PropTypes.bool,

  /** Determines if the "Recents" menu hides the "Add Connected Person" control. */
  readOnlyMode: PropTypes.bool,

  /** Override default on tree item click behavior; tree navigation menu overlay is still closed afterward.  */
  onTreeNavClickOverride: PropTypes.func,
}
